import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import AnnouncementBanner from '../../components/ui/AnnouncementBanner';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { auditReportService } from '../../services/auditReportService';

import ResultsHeader from './components/ResultsHeader';
import ResultsSidebar, { sidebarSections } from './components/ResultsSidebar';
import ScoreOverview from './components/ScoreOverview';
import SlideAnalysis from './components/SlideAnalysis';
import AllIssues from './components/AllIssues';
import Recommendations from './components/Recommendations';
import CompetitorBenchmark from './components/CompetitorBenchmark';
import IndustryTrends from './components/IndustryTrends';
import PersonaMatch from './components/PersonaMatch';
import MarketAnalysis from './components/MarketAnalysis';
import FoundersNote from './components/FoundersNote';
import PitchCanvas from './components/PitchCanvas';
import BattleCards from './components/BattleCards';
import OutreachSequence from './components/OutreachSequence';
import KeyDocuments from './components/KeyDocuments';
import CustomPrompts from './components/CustomPrompts';
import PremiumGate from './components/PremiumGate';
import AuditDisclaimer from './components/AuditDisclaimer';
import { getScoreNum } from './components/shared';

const BLUE = '#2563eb';
const GRADIENT = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';

// ── Responsive content padding ──────────────────────────────
function useContentPad() {
  const [pad, setPad] = useState('16px 12px');
  useEffect(() => {
    const update = () => setPad(
      window.innerWidth < 640  ? '16px 12px' :
      window.innerWidth < 768  ? '20px 16px' :
      window.innerWidth < 1024 ? '24px 20px' :
                                  '28px 32px'
    );
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return pad;
}

// ── Responsive breakpoint hook ──────────────────────────────
function useResponsive() {
  const [breakpoint, setBreakpoint] = useState({
    isMobile: window.innerWidth < 640,
    isTablet: window.innerWidth >= 640 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024,
    width: window.innerWidth
  });

  useEffect(() => {
    let timeoutId;
    let lastWidth = window.innerWidth;
    
    const update = () => {
      const width = window.innerWidth;
      // Only update if width actually changed significantly (ignore height changes from scroll)
      if (Math.abs(width - lastWidth) < 10) return;
      lastWidth = width;
      
      setBreakpoint({
        isMobile: width < 640,
        isTablet: width >= 640 && width < 1024,
        isDesktop: width >= 1024,
        width
      });
    };
    
    const debouncedUpdate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(update, 150); // Debounce by 150ms
    };

    window.addEventListener('resize', debouncedUpdate);
    return () => {
      window.removeEventListener('resize', debouncedUpdate);
      clearTimeout(timeoutId);
    };
  }, []);

  return breakpoint;
}

// ══════════════════════════════════════════════════════════════
//  parseJsonReport (unchanged)
// ══════════════════════════════════════════════════════════════
function parseJsonReport(jd) {
  if (!jd) return null;

  const result = {
    startupData: {},
    slides: [],
    groupedPriorities: { HIGH: [], MEDIUM: [], LOW: [] },
    recommendations: [],
    categoryScores: [],
    personaInsights: [],
    battleCards: [],
    outreachSequence: [],
    competitorData: [],
    marketData: {},
    versionHistory: [],
    benchmarkData: {},
    foundersNote: {},
    pitchCanvasData: [],
    eDocs: [],
    customPrompts: [],
  };

  const cover = jd.cover || jd.COVER || {};
  const meta = jd.meta || jd.META || {};
  const pc = jd.pitch_canvas || jd.PITCH_CANVAS || {};

  result.startupData = {
    iltc_score: String(cover.SCORE || cover.iltc_score || cover.score || ''),
    deck_tier: cover.TIER_LABEL || cover.TIER || cover.tier || '',
    deck_title: cover.DECK_NAME || cover.DECK_TITLE || cover.deck_title || '',
    startup_name: cover.COMPANY_NAME || cover.STARTUP_NAME || cover.startup_name || '',
    report_date: meta.REPORT_DATE || cover.REPORT_DATE || '',
    report_type: jd.report_type || '',
    slide_usage: String(cover.TOTAL_SLIDES || cover.total_slides || cover.slide_usage || ''),
    industry: pc.INDUSTRY || cover.INDUSTRY || '',
    industry_sub: pc.INDUSTRY_SUB || '',
    funding_stage: pc.FUNDING_STAGE || cover.FUNDING_STAGE || '',
    funding_target: pc.FUNDING_ASK || cover.FUNDING_ASK || '',
    business_model: pc.BUSINESS_MODEL || '',
    founded_year: pc.FOUNDED_YEAR || '',
    value_proposition: pc.VALUE_PROPOSITION || '',
    founding_team: pc.FOUNDING_TEAM || '',
    customer_segments: pc.CUSTOMER_SEGMENTS || '',
    about: pc.ABOUT_COMPANY || '',
  };

  const canvasMap = [
    { label: 'Business Model', key: 'BUSINESS_MODEL' },
    { label: 'Founded In', key: 'FOUNDED_YEAR' },
    { label: 'Value Proposition', key: 'VALUE_PROPOSITION' },
    { label: 'Funding Stage', key: 'FUNDING_STAGE' },
    { label: 'Founding Team', key: 'FOUNDING_TEAM' },
    { label: 'Industry', key: 'INDUSTRY' },
    { label: 'Funding Ask', key: 'FUNDING_ASK' },
    { label: 'Customer Segments', key: 'CUSTOMER_SEGMENTS' },
    { label: 'About', key: 'ABOUT_COMPANY' },
    { label: 'Segments Detail', key: 'CUSTOMER_SEGMENTS_DETAIL' },
  ];
  canvasMap.forEach(({ label, key }) => {
    const val = pc[key];
    if (val) result.pitchCanvasData.push({ label, value: val });
  });
  if (pc.INDUSTRY_SUB) {
    const idx = result.pitchCanvasData.findIndex(c => c.label === 'Industry');
    if (idx >= 0) result.pitchCanvasData[idx].value += ` · ${pc.INDUSTRY_SUB}`;
  }

  const so = jd.score_overview || jd.SCORE_OVERVIEW || {};
  const dims = so.dimensions || jd.dimensions || jd.DIMENSIONS || jd.dimension_scores || {};
  if (dims && typeof dims === 'object' && !Array.isArray(dims)) {
    const map = {
      narrative: 'Narrative',
      visual_design: 'Visual Design',
      data_evidence: 'Data & Evidence',
      structure_flow: 'Structure & Flow',
      persuasion_cta: 'Persuasion & CTA',
    };
    Object.entries(map).forEach(([key, label]) => {
      const d = dims[key];
      if (d) {
        const score = d.DIM1_SCORE ?? d.DIM2_SCORE ?? d.DIM3_SCORE ?? d.DIM4_SCORE ?? d.DIM5_SCORE ?? d.score ?? 0;
        result.categoryScores.push({ name: label, score: String(score) });
      }
    });
  } else if (Array.isArray(dims)) {
    result.categoryScores = dims.map(d => ({
      name: d.name || d.DIMENSION || '',
      score: String(d.score || d.SCORE || 0),
    }));
  }

  const sa = jd.slide_analysis || jd.SLIDE_ANALYSIS || {};
  const rawSlides = sa.slides || jd.slides || [];
  result.slides = rawSlides.map(s => ({
    slider_sr_no: String(s.SLIDE_NUM || s.slide_num || s.num || ''),
    slider_name: s.SLIDE_TITLE || s.slide_title || s.title || '',
    slider_type: s.SLIDE_TYPE || s.slide_type || s.type || '',
    slider_score: String(s.SLIDE_SCORE || s.slide_score || s.score || ''),
    solutions: s.works || s.WORKS || [],
    issues: s.doesnt_work || s.DOESNT_WORK || s.issues || [],
  }));

  const ci = jd.all_issues || jd.critical_issues || jd.CRITICAL_ISSUES || [];
  ci.forEach(i => {
    const rawLevel = (i.priority || i.PRIORITY || i.priority_level || 'HIGH').toString().toUpperCase().trim();
    const level = rawLevel.startsWith('H') ? 'HIGH' : rawLevel.startsWith('L') ? 'LOW' : 'MEDIUM';
    result.groupedPriorities[level].push({
      ISSUE_TITLE: i.ISSUE_TITLE || i.title || '',
      ISSUE_DESC: i.ISSUE_DESC || i.ISSUE_DESCRIPTION || i.description || '',
      ISSUE_SLIDES: i.ISSUE_SLIDES || i.slide_ref || '',
      priority: i.priority || (level.charAt(0) + level.slice(1).toLowerCase()),
      priority_action_name: i.ISSUE_TITLE || i.title || '',
      priority_action_details: i.ISSUE_DESC || i.ISSUE_DESCRIPTION || i.description || '',
      priority_level: level,
      slide_ref: i.ISSUE_SLIDES || i.slide_ref || '',
    });
  });

  const recs = jd.recommendations || jd.RECOMMENDATIONS || [];
  result.recommendations = recs.map(r => ({
    priority_action_name: r.REC_TITLE || r.TITLE || r.title || '',
    priority_action_details: r.REC_DESCRIPTION || r.DESCRIPTION || r.description || '',
    impact: r.impact || r.IMPACT || '',
    effort: r.REC_EFFORT || r.EFFORT || r.effort || '',
    tag: r.REC_DIMENSION || r.TAG || r.tag || r.DIMENSION || '',
  }));

  const compRoot = jd.competitor_benchmarks || jd.competitor_benchmark || jd.COMPETITOR_BENCHMARK || jd.competitors || null;
  if (compRoot) {
    if (Array.isArray(compRoot)) {
      result.competitorData = compRoot.map(c => ({
        name: c.NAME || c.name || '',
        overall: String(c.OVERALL || c.overall || ''),
        narrative: String(c.NARRATIVE || c.narrative || ''),
        design: String(c.DESIGN || c.design || ''),
        data: String(c.DATA || c.data || ''),
        structure: String(c.STRUCTURE || c.structure || ''),
        persuasion: String(c.PERSUASION || c.persuasion || ''),
        isYou: c.IS_YOU || c.isYou || false,
      }));
    } else if (typeof compRoot === 'object') {
      const yours = compRoot.your_company || {};
      const comps = compRoot.competitors || [];
      const avg = compRoot.industry_average || {};
      const mapC = (c, isYou = false) => ({
        name: c.COMP_NAME || c.COMPANY_NAME || c.name || '',
        overall: String(c.COMP_SCORE || c.SCORE || c.overall || ''),
        narrative: String(c.COMP_D1 || c.DIM1_SCORE || c.narrative || ''),
        design: String(c.COMP_D2 || c.DIM2_SCORE || c.design || ''),
        data: String(c.COMP_D3 || c.DIM3_SCORE || c.data || ''),
        structure: String(c.COMP_D4 || c.DIM4_SCORE || c.structure || ''),
        persuasion: String(c.COMP_D5 || c.DIM5_SCORE || c.persuasion || ''),
        isYou,
      });
      if (Object.keys(yours).length) result.competitorData.push(mapC(yours, true));
      comps.forEach(c => result.competitorData.push(mapC(c, false)));
      if (Object.keys(avg).length) {
        result.benchmarkData = {
          yourScore: String(yours.SCORE || yours.COMP_SCORE || ''),
          industryAvg: String(avg.AVG_SCORE || ''),
          industry: String(avg.AVG_SCORE || ''),
          topDecile: '',
          avgD1: String(avg.AVG_D1 || ''),
          avgD2: String(avg.AVG_D2 || ''),
          avgD3: String(avg.AVG_D3 || ''),
          avgD4: String(avg.AVG_D4 || ''),
          avgD5: String(avg.AVG_D5 || ''),
        };
      }
    }
  }

  const mkt = jd.market_analysis || jd.MARKET_ANALYSIS || {};
  const itrd = jd.industry_trends || jd.INDUSTRY_TRENDS || {};

  const normaliseItem = (i) => {
    if (typeof i === 'string') {
      const parts = i.split(/[.—:]/);
      return { title: parts[0]?.trim() || '', body: parts.slice(1).join('.').trim() || i };
    }
    return {
      title: i.title || i.text?.split(/[.—:]/)[0]?.trim() || '',
      body: i.body || i.description || i.text?.split(/[.—:]/)[1]?.trim() || i.text || '',
    };
  };

  const rawTrends = mkt.trends || mkt.TRENDS || itrd.trends || itrd.TRENDS || [];
  result.marketData.trends = Array.isArray(rawTrends) ? rawTrends.map(normaliseItem) : [];

  const rawRisks = mkt.risks || mkt.RISKS || itrd.risks || itrd.RISKS || [];
  result.marketData.risks = Array.isArray(rawRisks) ? rawRisks.map(normaliseItem) : [];

  const overview = mkt.MARKET_OVERVIEW || mkt.overview || mkt.OVERVIEW || itrd.overview || '';
  if (overview) result.marketData.overview = overview;

  const rawInsights = mkt.insights || mkt.INSIGHTS || itrd.insights || [];
  result.marketData.insights = Array.isArray(rawInsights) ? rawInsights.map(normaliseItem) : [];

  const personas = jd.audience_personas || jd.persona_insights || jd.PERSONA_INSIGHTS || jd.personas || [];
  result.personaInsights = personas.map(p => ({
    name: p.PERSONA_ROLE || p.NAME || p.name || p.ROLE || p.role || '',
    desc: p.PERSONA_DESC || p.DESC || p.desc || p.DETAIL || p.detail || '',
    score: String(p.PERSONA_MATCH || p.SCORE || p.score || p.MATCH || p.match || ''),
    tags: p.TAGS || p.tags || [],
  }));

  const bc = jd.battle_cards || jd.BATTLE_CARDS || jd.battleCards || [];
  result.battleCards = bc.map(b => ({
    title: b.BC_COMPETITOR || b.TITLE || b.title || b.NAME || b.name || '',
    strength: b.BC_STRENGTH || b.STRENGTH || b.strength || '',
    weakness: b.BC_WEAKNESS || b.WEAKNESS || b.weakness || '',
    differentiator: b.BC_DIFF || b.DIFFERENTIATOR || b.differentiator || '',
    winStrategy: b.BC_WIN || b.WIN_STRATEGY || b.winStrategy || b.WIN || '',
  }));

  const osRaw = jd.outreach_sequences || jd.outreach_sequence || jd.OUTREACH_SEQUENCE || jd.outreachSequence || null;
  if (osRaw) {
    if (Array.isArray(osRaw)) {
      result.outreachSequence = osRaw.map((s, i) => ({
        step: i + 1,
        channel: s.CHANNEL || s.channel || '',
        subject: s.SUBJECT || s.subject || '',
        body: s.BODY || s.body || s.CONTENT || s.content || '',
      }));
    } else if (typeof osRaw === 'object') {
      const steps = [];
      if (osRaw.EMAIL_SUBJECT || osRaw.EMAIL_BODY)
        steps.push({ step: 1, channel: 'Email', subject: osRaw.EMAIL_SUBJECT || '', body: osRaw.EMAIL_BODY || '' });
      if (osRaw.WHATSAPP_MSG)
        steps.push({ step: 2, channel: 'WhatsApp', subject: '', body: osRaw.WHATSAPP_MSG });
      if (osRaw.CALL_OPENING || osRaw.CALL_HOOK)
        steps.push({ step: 3, channel: 'Phone Call', subject: 'Call Opening', body: [osRaw.CALL_OPENING, osRaw.CALL_HOOK].filter(Boolean).join('\n\n') });
      result.outreachSequence = steps;
    }
  }

  const docsRaw = jd.key_documents || jd.KEY_DOCUMENTS || jd.eDocs || jd.artefacts || null;
  if (docsRaw) {
    if (Array.isArray(docsRaw)) {
      result.eDocs = docsRaw.map(d => ({
        title: d.TITLE || d.title || '',
        type: d.TYPE || d.type || '',
        content: d.CONTENT || d.content || '',
        faqs: d.FAQS || d.faqs || [],
      }));
    } else if (typeof docsRaw === 'object') {
      if (docsRaw.PR_CONTENT)
        result.eDocs.push({ title: 'Press Release', type: 'PR', content: docsRaw.PR_CONTENT, faqs: [] });
      if (docsRaw.faqs?.length)
        result.eDocs.push({
          title: 'FAQ Document', type: 'FAQ', content: '',
          faqs: docsRaw.faqs.map(f => ({ question: f.FAQ_Q || f.question || '', answer: f.FAQ_A || f.answer || '' })),
        });
    }
  }

  const cpRaw = jd.deck_prompts || jd.custom_prompts || jd.CUSTOM_PROMPTS || jd.customPrompts || null;
  if (cpRaw) {
    if (Array.isArray(cpRaw)) {
      result.customPrompts = cpRaw.map(p => ({ label: p.LABEL || p.label || '', text: p.TEXT || p.text || '' }));
    } else if (typeof cpRaw === 'object') {
      if (cpRaw.MASTER_PROMPT)
        result.customPrompts.push({ label: 'Master Rebuild Prompt', text: cpRaw.MASTER_PROMPT });
      (cpRaw.slide_prompts || cpRaw.SLIDE_PROMPTS || []).forEach(p =>
        result.customPrompts.push({
          label: `Slide ${p.SLIDE_NUM || ''}: ${p.SLIDE_TITLE || ''}`.trim(),
          text: p.SLIDE_PROMPT || p.TEXT || p.text || '',
        })
      );
    }
  }

  const fn = jd.founders_note || jd.FOUNDERS_NOTE || jd.foundersNote || {};
  if (Object.keys(fn).length) {
    result.foundersNote = {
      eyebrow: fn.EYEBROW || fn.eyebrow || "Founder's Note",
      subtitle: fn.SUBTITLE || fn.subtitle || '',
      authorName: fn.AUTHOR_NAME || fn.authorName || fn.author_name || '',
      authorRole: fn.AUTHOR_ROLE || fn.authorRole || fn.author_role || '',
      authorInitials: fn.AUTHOR_INITIALS || fn.authorInitials || fn.initials || '',
      bigQuote: fn.BIG_QUOTE || fn.bigQuote || fn.big_quote || '',
      principles: fn.PRINCIPLES || fn.principles || [],
      campbell: fn.CAMPBELL || fn.campbell || '',
      steps: fn.STEPS || fn.steps || [],
    };
  }

  const testimonials = jd.testimonials || jd.TESTIMONIALS || [];
  if (testimonials.length) {
    result.marketData.testimonials = testimonials.map(t => ({
      text: t.TEXT || t.text || '',
      name: t.NAME || t.name || '',
      role: t.ROLE || t.role || '',
      initials: t.INITIALS || t.initials || '',
    }));
  }

  result.versionHistory = jd.version_history || jd.VERSION_HISTORY || jd.versionHistory || [];

  if (!Object.keys(result.benchmarkData).length) {
    const bm = jd.benchmark_data || jd.BENCHMARK_DATA || jd.benchmarkData || {};
    result.benchmarkData = {
      yourScore: bm.YOUR_SCORE || bm.yourScore || '',
      industry: bm.INDUSTRY || bm.industry || '',
      topDecile: bm.TOP_DECILE || bm.topDecile || '',
    };
  }

  return result;
}

// ══════════════════════════════════════════════════════════════
//  parseReportDataFromHtml (unchanged - keeping original)
// ══════════════════════════════════════════════════════════════
function parseReportDataFromHtml(htmlContent) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const result = {
    startupData: {}, slides: [], groupedPriorities: { HIGH: [], MEDIUM: [], LOW: [] },
    recommendations: [], categoryScores: [], personaInsights: [], battleCards: [],
    outreachSequence: [], competitorData: [], marketData: {}, versionHistory: [],
    benchmarkData: {}, foundersNote: {}, pitchCanvasData: [], eDocs: [], customPrompts: []
  };
  try {
    result.startupData.startup_name = doc.querySelector('.cover-deck-name')?.textContent?.trim() || '';
    result.startupData.iltc_score = doc.querySelector('.cover-score-number')?.textContent?.trim() || '';
    result.startupData.deck_title = doc.querySelector('.cover-title')?.textContent?.trim() || '';
    result.startupData.deck_tier = doc.querySelector('.cover-tier')?.textContent?.trim() || '';
    result.startupData.report_date = doc.querySelector('.cover-date')?.textContent?.trim() || '';
    result.startupData.report_type = doc.querySelector('.cover-report-type')?.textContent?.trim() || '';
    doc.querySelectorAll('.cover-meta-item').forEach(item => {
      const label = item.childNodes[0]?.textContent?.trim().toLowerCase().replace(':', '');
      const val = item.querySelector('span')?.textContent?.trim();
      if (!val) return;
      if (label?.includes('slide')) result.startupData.slide_usage = val;
      if (label?.includes('deck type') || label?.includes('type')) result.startupData.deck_type = val;
      if (label?.includes('industry')) result.startupData.industry = val;
    });

    const fnSection = doc.querySelector('.founders-note');
    if (fnSection) {
      result.foundersNote = {
        eyebrow: fnSection.querySelector('.fn-eyebrow')?.textContent?.trim() || "Founder's Note",
        subtitle: fnSection.querySelector('.fn-subtitle')?.textContent?.trim() || '',
        authorName: fnSection.querySelector('.fn-author-name')?.textContent?.trim() || '',
        authorRole: fnSection.querySelector('.fn-author-role')?.textContent?.trim() || '',
        authorInitials: fnSection.querySelector('.fn-author-photo')?.textContent?.trim() || '',
        bigQuote: fnSection.querySelector('.fn-big-quote')?.textContent?.trim() || '',
        principles: Array.from(fnSection.querySelectorAll('.fn-principle')).map(p => p.innerHTML?.trim() || ''),
        campbell: fnSection.querySelector('.fn-campbell')?.textContent?.trim() || '',
        steps: Array.from(fnSection.querySelectorAll('.fn-step')).map(s => ({
          num: s.querySelector('.fn-step-num')?.textContent?.trim() || '',
          title: s.querySelector('h5')?.textContent?.trim() || '',
          desc: s.querySelector('p')?.textContent?.trim() || '',
        })),
      };
    }

    const canvasCells = [];
    doc.querySelectorAll('.canvas-cell').forEach(cell => {
      const label = cell.querySelector('.canvas-cell-label')?.textContent?.trim() || '';
      const value = cell.querySelector('.canvas-cell-value')?.textContent?.trim() || '';
      if (label && value) canvasCells.push({ label, value });
      const l = label.toLowerCase();
      if (l.includes('funding ask')) result.startupData.funding_target = value;
      if (l.includes('business model')) result.startupData.business_model = value;
      if (l.includes('funding stage')) result.startupData.funding_stage = value;
      if (l.includes('founded')) result.startupData.founded_year = value;
      if (l.includes('value prop')) result.startupData.value_proposition = value;
      if (l.includes('founding team')) result.startupData.founding_team = value;
      if (l.includes('customer segment')) result.startupData.customer_segments = value;
    });
    doc.querySelectorAll('.canvas-bottom-cell').forEach(cell => {
      const label = cell.querySelector('.canvas-cell-label')?.textContent?.trim() || '';
      const value = cell.querySelector('.canvas-cell-value')?.textContent?.trim() || '';
      if (label && value) canvasCells.push({ label, value });
      const l = label.toLowerCase();
      if (l.includes('about')) result.startupData.about = value;
      if (l.includes('customer segment') && !result.startupData.customer_segments)
        result.startupData.customer_segments = value;
    });
    result.pitchCanvasData = canvasCells;

    doc.querySelectorAll('.dim-row').forEach(row => {
      const name = row.querySelector('.dim-name')?.textContent?.trim();
      const score = row.querySelector('.dim-score')?.textContent?.trim();
      if (name && score) result.categoryScores.push({ name, score });
    });

    doc.querySelectorAll('.slide-card').forEach((card, idx) => {
      const solutions = [], issues = [];
      card.querySelector('.slide-col-label.works')?.closest('.slide-col')
        ?.querySelectorAll('.slide-point').forEach(pt => { const t = pt.textContent?.trim(); if (t) solutions.push(t); });
      card.querySelector('.slide-col-label.doesnt')?.closest('.slide-col')
        ?.querySelectorAll('.slide-point').forEach(pt => { const t = pt.textContent?.trim(); if (t) issues.push(t); });
      result.slides.push({
        slider_sr_no: card.querySelector('.slide-number')?.textContent?.trim() || `${idx + 1}`,
        slider_name: card.querySelector('.slide-name')?.textContent?.trim() || '',
        slider_type: card.querySelector('.slide-type')?.textContent?.trim() || '',
        slider_score: card.querySelector('.slide-score-badge')?.textContent?.trim() || '',
        issues, solutions,
      });
    });

    doc.querySelectorAll('.issues-table tbody tr').forEach(row => {
      const priorityEl = row.querySelector('.issue-priority-badge');
      const titleEl = row.querySelector('.issue-title-cell');
      if (!priorityEl || !titleEl) return;
      const priority = priorityEl.textContent?.trim().toUpperCase();
      const issue = {
        ISSUE_TITLE: titleEl.textContent?.trim(),
        ISSUE_DESC: row.querySelector('.issue-desc-cell')?.textContent?.trim(),
        ISSUE_SLIDES: row.querySelector('.issue-slide-cell')?.textContent?.trim(),
        priority_action_name: titleEl.textContent?.trim(),
        priority_action_details: row.querySelector('.issue-desc-cell')?.textContent?.trim(),
        priority_level: priority,
        slide_ref: row.querySelector('.issue-slide-cell')?.textContent?.trim(),
      };
      if (priority === 'HIGH') result.groupedPriorities.HIGH.push(issue);
      else if (priority === 'LOW') result.groupedPriorities.LOW.push(issue);
      else result.groupedPriorities.MEDIUM.push(issue);
    });

    doc.querySelectorAll('.rec-card').forEach(card => {
      const title = card.querySelector('h4')?.textContent?.trim();
      if (title) result.recommendations.push({
        priority_action_name: title,
        priority_action_details: card.querySelector('p')?.textContent?.trim(),
        impact: card.querySelector('.rec-impact')?.textContent?.trim() || '',
        effort: card.querySelector('.rec-effort')?.textContent?.trim() || '',
        tag: card.querySelector('.rec-dimension-tag')?.textContent?.trim() || '',
      });
    });

    doc.querySelectorAll('.benchmark-table tbody tr').forEach(row => {
      const cells = Array.from(row.querySelectorAll('td'));
      if (cells.length >= 2) {
        const name = cells[0]?.textContent?.trim();
        if (name) result.competitorData.push({
          name,
          overall: cells[1]?.textContent?.trim(),
          narrative: cells[2]?.textContent?.trim(),
          design: cells[3]?.textContent?.trim(),
          data: cells[4]?.textContent?.trim(),
          structure: cells[5]?.textContent?.trim(),
          persuasion: cells[6]?.textContent?.trim(),
          isYou: row.classList.contains('your-row'),
        });
      }
    });
    const yourRow = doc.querySelector('.benchmark-table .your-row');
    if (yourRow) {
      const cells = Array.from(yourRow.querySelectorAll('td'));
      result.benchmarkData.yourScore = cells[1]?.textContent?.trim();
      const avgRow = Array.from(doc.querySelectorAll('.benchmark-table tbody tr'))
        .find(r => r.textContent?.toLowerCase().includes('industry average'));
      if (avgRow)
        result.benchmarkData.industry = Array.from(avgRow.querySelectorAll('td'))[1]?.textContent?.trim();
      const otherRows = Array.from(doc.querySelectorAll('.benchmark-table tbody tr'))
        .filter(r => !r.classList.contains('your-row') && !r.textContent?.toLowerCase().includes('industry average'));
      if (otherRows.length) {
        const scores = otherRows.map(r => parseInt(Array.from(r.querySelectorAll('td'))[1]?.textContent) || 0);
        result.benchmarkData.topDecile = String(Math.max(...scores));
      }
    }

    const sections = Array.from(doc.querySelectorAll('.report-section'));
    const industrySection = sections.find(s =>
      s.querySelector('.section-title')?.textContent?.includes('Industry Trends')
    );
    if (industrySection) {
      const trends = [], risks = [];
      industrySection.querySelectorAll('.intel-card').forEach(card => {
        const heading = card.querySelector('h4')?.textContent?.trim() || '';
        card.querySelectorAll('li').forEach(li => {
          const text = li.textContent?.trim();
          if (!text) return;
          if (heading.toLowerCase().includes('risk')) risks.push(text);
          else trends.push(text);
        });
      });
      if (trends.length) result.marketData.trends = trends;
      if (risks.length) result.marketData.risks = risks;
    }

    const marketSection = sections.find(s =>
      s.querySelector('.section-title')?.textContent?.includes('Market Specific')
    );
    if (marketSection) {
      const insights = [];
      marketSection.querySelectorAll('.intel-card').forEach(card => {
        const para = card.querySelector('p')?.textContent?.trim();
        if (para) result.marketData.overview = para;
        card.querySelectorAll('li').forEach(li => {
          const t = li.textContent?.trim();
          if (t) insights.push(t);
        });
      });
      if (insights.length) result.marketData.insights = insights;
    }

    doc.querySelectorAll('.persona-card').forEach(card => {
      const role = card.querySelector('.persona-role')?.textContent?.trim();
      const detail = card.querySelector('.persona-detail')?.textContent?.trim();
      const match = card.querySelector('.persona-match')?.textContent?.trim();
      const scoreM = match?.match(/(\d+)%/);
      if (role) result.personaInsights.push({ name: role, desc: detail, score: scoreM ? scoreM[1] : '', tags: [] });
    });

    doc.querySelectorAll('.battlecard').forEach(card => {
      const name = card.querySelector('.battlecard-name')?.textContent?.trim();
      if (!name) return;
      const rows = {};
      card.querySelectorAll('.battlecard-row').forEach(row => {
        const key = row.querySelector('.battlecard-key')?.textContent?.trim().toLowerCase() || '';
        const val = row.querySelector('.battlecard-val')?.textContent?.trim() || '';
        if (key.includes('strength')) rows.strength = val;
        if (key.includes('weakness')) rows.weakness = val;
        if (key.includes('differentiator')) rows.differentiator = val;
      });
      const win = card.querySelector('.battlecard-win')?.textContent?.trim() || '';
      if (!result.battleCards.find(b => b.title === name))
        result.battleCards.push({ title: name, ...rows, winStrategy: win });
    });

    doc.querySelectorAll('.sequence-step').forEach((step, i) => {
      const channel = step.querySelector('.step-channel')?.textContent?.trim() || '';
      const content = step.querySelector('.step-content');
      
      if (!content) return;
      
      // Clone the content to avoid modifying the original
      const contentClone = content.cloneNode(true);
      
      // Get the subject from the strong tag
      const strongElement = contentClone.querySelector('strong');
      let subject = '';
      
      if (strongElement) {
        let fullStrongText = strongElement.textContent?.trim() || '';
        // Remove "Subject:" prefix if present
        subject = fullStrongText
          .replace(/^Subject:\s*/i, '')  // Remove "Subject:" case-insensitive
          .split('\n')[0]                // Get first line
          ?.trim() || '';
        // Remove the strong element from the clone
        strongElement.remove();
      }
      
      // Get the remaining HTML content and convert <br> tags to newlines
      let bodyHtml = contentClone.innerHTML?.trim() || '';
      
      // Convert <br> tags to \n
      let body = bodyHtml
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/&nbsp;/g, ' ')
        .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
        .trim();
      
      // Clean up multiple newlines (optional: keep them as they are for proper formatting)
      // body = body.replace(/\n{3,}/g, '\n\n'); // Uncomment if you want to limit to double newlines
      
      if (channel || body) {
        result.outreachSequence.push({ 
          step: i + 1, 
          channel, 
          subject, 
          body
        });
      }
    });

    doc.querySelectorAll('.artefact-block').forEach(block => {
      const title = block.querySelector('h4')?.textContent?.trim() || '';
      const type = block.querySelector('.artefact-type')?.textContent?.trim() || '';
      const content = block.querySelector('.artefact-content')?.innerText?.trim()
        || block.querySelector('.artefact-content')?.textContent?.trim() || '';
      const faqs = [];
      block.querySelectorAll('.artefact-content h5').forEach(h5 => {
        if (h5.textContent?.trim().startsWith('Q:')) {
          const q = h5.textContent?.trim();
          const a = h5.nextElementSibling?.textContent?.trim();
          if (q && a) faqs.push({ question: q, answer: a });
        }
      });
      if (title) result.eDocs.push({ title, type, content, faqs });
    });
    const allFaqs = result.eDocs.flatMap(d => d.faqs || []);
    if (allFaqs.length) result.marketData.faqs = allFaqs;

    doc.querySelectorAll('.keydoc-item').forEach(item => {
      const name = item.querySelector('.keydoc-name')?.textContent?.trim();
      const desc = item.querySelector('.keydoc-desc')?.textContent?.trim();
      const icon = item.querySelector('.keydoc-icon')?.textContent?.trim();
      if (name) result.marketData.keyDocs = [...(result.marketData.keyDocs || []), { name, desc, icon }];
    });

    doc.querySelectorAll('.prompt-block').forEach(block => {
      const label = block.querySelector('.prompt-label')?.textContent?.trim() || '';
      const text = block.querySelector('.prompt-text')?.textContent?.trim() || '';
      if (text) result.customPrompts.push({ label, text });
    });

  } catch (e) { console.error('parseReportDataFromHtml error:', e); }
  return result;
}

// ══════════════════════════════════════════════════════════════
//  extractSlideHtmlMap (unchanged)
// ══════════════════════════════════════════════════════════════
function extractSlideHtmlMap(htmlContent) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const headStyles = Array.from(doc.querySelectorAll('style, link[rel="stylesheet"]'))
    .map(el => el.outerHTML).join('\n');
  return Array.from(doc.querySelectorAll('.slide-card')).map(card =>
    `<!DOCTYPE html>
<html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/>
${headStyles}
<style>*{box-sizing:border-box;}html,body{margin:0;padding:0;background:transparent;}body{padding:16px;}.slide-card{margin:0!important;border-radius:12px;overflow:hidden;}</style>
</head><body>${card.outerHTML}</body></html>`
  );
}

// ══════════════════════════════════════════════════════════════
//  fetchHtmlFromStorage (unchanged)
// ══════════════════════════════════════════════════════════════
async function fetchHtmlFromStorage(storagePath) {
  const path = storagePath.replace(/^html-reports\//, '').trim();
  try {
    const { data, error } = await supabase.storage.from('html-reports').download(path);
    if (!error && data) { const text = await data.text(); return text; }
  } catch (e) { console.warn('[HTML Storage] download() threw:', e?.message); }
  try {
    const { data: signedData, error: signedError } = await supabase.storage.from('html-reports').createSignedUrl(path, 300);
    if (!signedError && signedData?.signedUrl) {
      const res = await fetch(signedData.signedUrl, { cache: 'no-store' });
      if (res.ok) return await res.text();
    }
  } catch (e) { console.warn('[HTML Storage] createSignedUrl threw:', e?.message); }
  try {
    const { data: pubData } = supabase.storage.from('html-reports').getPublicUrl(path);
    if (pubData?.publicUrl) {
      const res = await fetch(pubData.publicUrl, { cache: 'no-store' });
      if (res.ok) return await res.text();
    }
  } catch (e) { console.warn('[HTML Storage] getPublicUrl threw:', e?.message); }
  return '';
}

// ══════════════════════════════════════════════════════════════
//  FreeAllIssues (unchanged - already mobile responsive)
// ══════════════════════════════════════════════════════════════
const F_FREE = {
  display: "'Plus Jakarta Sans', system-ui, sans-serif",
  body: "'DM Sans', system-ui, sans-serif",
  mono: "'JetBrains Mono', monospace",
};
const PAGE_SIZE_FREE = 3;

function FreeAllIssues({ groupedPriorities, issueFilter, setIssueFilter, issueCurrentPage, setIssueCurrentPage }) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const highIssues = groupedPriorities?.HIGH || [];
  const medIssues  = groupedPriorities?.MEDIUM || [];
  const lowIssues  = groupedPriorities?.LOW || [];
  const totalAll   = highIssues.length + medIssues.length + lowIssues.length;

  const allIssuesList =
    issueFilter === 'HIGH'   ? highIssues :
    issueFilter === 'MEDIUM' ? medIssues  :
    issueFilter === 'LOW'    ? lowIssues  :
    [...highIssues, ...medIssues, ...lowIssues];

  const isTabGated  = issueFilter === 'MEDIUM' || issueFilter === 'LOW';
  const isPageGated = issueCurrentPage > 1;

  const fakeTotal  = Math.max(allIssuesList.length, PAGE_SIZE_FREE + 1);
  const totalPages = Math.max(1, Math.ceil(fakeTotal / PAGE_SIZE_FREE));
  const safePage   = Math.min(issueCurrentPage, totalPages);
  const visibleIssues = allIssuesList.slice(0, PAGE_SIZE_FREE);

  const getPageNumbers = () => {
    const pages = [];
    for (let p = 1; p <= totalPages; p++) {
      if (p === 1 || p === totalPages || Math.abs(p - safePage) <= 1) pages.push(p);
      else if (pages[pages.length - 1] !== '...') pages.push('...');
    }
    return pages;
  };

  const getPriorityStyle = (item) => {
    const p = (item.priority_level || item.priority || '').toUpperCase();
    if (p === 'HIGH') return { label: 'High',   color: '#dc2626', bg: '#fef2f2' };
    if (p === 'LOW')  return { label: 'Low',    color: '#16a34a', bg: '#f0fdf4' };
    return                   { label: 'Medium', color: '#d97706', bg: '#fef3c7' };
  };

  const PaginationBar = () => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexWrap: 'wrap', gap: 6,
      padding: '12px 16px',
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      borderTop: 'none',
      borderRadius: '0 0 16px 16px',
    }}>
      <button onClick={() => setIssueCurrentPage(p => Math.max(1, p - 1))} disabled={safePage === 1}
        style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: safePage === 1 ? 'not-allowed' : 'pointer', opacity: safePage === 1 ? 0.3 : 1, fontFamily: F_FREE.body, fontSize: 15, color: '#374151', flexShrink: 0 }}>‹</button>
      {getPageNumbers().map((p, idx) =>
        p === '...' ? (
          <span key={`e-${idx}`} style={{ width: 32, textAlign: 'center', fontFamily: F_FREE.body, fontSize: 13, color: '#94a3b8' }}>…</span>
        ) : (
          <button key={p} onClick={() => setIssueCurrentPage(p)}
            style={{ width: 32, height: 32, borderRadius: 8, border: p === safePage ? 'none' : '1px solid #e2e8f0', background: p === safePage ? GRADIENT : '#ffffff', color: p === safePage ? '#ffffff' : '#374151', fontFamily: F_FREE.body, fontSize: 13, fontWeight: p === safePage ? 700 : 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {p}
          </button>
        )
      )}
      {/* <span style={{ padding: '0 10px', height: 32, display: 'flex', alignItems: 'center', fontFamily: F_FREE.body, fontSize: 11, fontWeight: 500, color: '#94a3b8', border: '1px solid #e2e8f0', borderRadius: 8, background: '#ffffff', flexShrink: 0 }}>
        {allIssuesList.length} issues
      </span> */}
      <button onClick={() => setIssueCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
        style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: safePage === totalPages ? 'not-allowed' : 'pointer', opacity: safePage === totalPages ? 0.3 : 1, fontFamily: F_FREE.body, fontSize: 15, color: '#374151', flexShrink: 0 }}>›</button>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, width: '100%', minWidth: 0 }}>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ fontFamily: F_FREE.display, fontSize: isMobile ? 18 : 22, fontWeight: 800, letterSpacing: '-0.03em', color: '#0f172a', margin: 0 }}>
          All Issues Identified
        </h2>
      </div>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, overflowX: 'auto', paddingBottom: 2, WebkitOverflowScrolling: 'touch' }}>
        {[
          { key: 'all',    label: isMobile ? `All (${totalAll})` : `All Issues (${totalAll})` },
          { key: 'HIGH',   label: isMobile ? `High (${highIssues.length})` : `High Priority (${highIssues.length})` },
          { key: 'MEDIUM', label: isMobile ? `Med (${medIssues.length})` : `Medium Priority (${medIssues.length})` },
          { key: 'LOW',    label: isMobile ? `Low (${lowIssues.length})` : `Low Priority (${lowIssues.length})` },
        ].map(f => (
          <button key={f.key} onClick={() => { setIssueFilter(f.key); setIssueCurrentPage(1); }}
            style={{
              padding: '5px 12px', borderRadius: 999, fontSize: isMobile ? 10.5 : 11, fontWeight: 600,
              fontFamily: F_FREE.body, cursor: 'pointer', transition: 'all 0.12s',
              border: issueFilter === f.key ? 'none' : '1px solid #e2e8f0',
              background: issueFilter === f.key ? GRADIENT : '#ffffff',
              color: issueFilter === f.key ? '#ffffff' : '#374151',
              flexShrink: 0, whiteSpace: 'nowrap',
            }}
          >{f.label}</button>
        ))}
      </div>

      {(isTabGated || isPageGated) ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <PremiumGate locked={true} sectionId="all-issues">{null}</PremiumGate>
          {totalPages > 1 && <PaginationBar />}
        </div>
      ) : (
        <>
          {isMobile ? (
            <div style={{
              background: '#ffffff', border: '1px solid #e2e8f0',
              borderRadius: totalPages > 1 ? '16px 16px 0 0' : '16px',
              overflow: 'hidden',
              width: '100%',
            }}>
              {visibleIssues.length > 0 ? visibleIssues.map((issue, i) => {
                const st = getPriorityStyle(issue);
                const issueTitle = issue.ISSUE_TITLE || issue.priority_action_name || '';
                const issueDesc  = issue.ISSUE_DESC  || issue.priority_action_details || '';
                const issueSlide = issue.ISSUE_SLIDES || issue.slide_ref || '';
                return (
                  <div key={i} style={{ padding: '14px 16px', borderBottom: i < visibleIssues.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
                      <p style={{ fontFamily: F_FREE.display, fontSize: 13, fontWeight: 700, color: '#0f172a', margin: 0, lineHeight: 1.4, flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{issueTitle}</p>
                      <span style={{ display: 'inline-flex', padding: '3px 8px', borderRadius: 20, fontFamily: F_FREE.display, fontSize: 10.5, fontWeight: 700, background: st.bg, color: st.color, flexShrink: 0 }}>{st.label}</span>
                    </div>
                    <p style={{ fontFamily: F_FREE.body, fontSize: 12, color: '#64748b', lineHeight: 1.6, margin: '0 0 6px', wordBreak: 'break-word' }}>{issueDesc}</p>
                    {issueSlide && <span style={{ fontFamily: F_FREE.mono, fontSize: 11, fontWeight: 600, color: '#004AAD' }}>↗ {issueSlide}</span>}
                  </div>
                );
              }) : (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 13, fontFamily: F_FREE.body }}>No issues found.</div>
              )}
            </div>
          ) : (
            <div style={{
              background: '#ffffff', border: '1px solid #e2e8f0',
              borderRadius: totalPages > 1 ? '16px 16px 0 0' : '16px',
              overflow: 'hidden', width: '100%',
            }}>
              {visibleIssues.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      {[
                        { h: '#',           w: '5%'  },
                        { h: 'Issue',       w: '22%' },
                        { h: 'Description', w: 'auto'},
                        { h: 'Priority',    w: '14%' },
                        { h: 'Slide',       w: '12%' },
                      ].map(({ h, w }) => (
                        <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontFamily: F_FREE.mono, fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', width: w }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleIssues.map((issue, i) => {
                      const st = getPriorityStyle(issue);
                      const issueTitle = issue.ISSUE_TITLE || issue.priority_action_name || '';
                      const issueDesc  = issue.ISSUE_DESC  || issue.priority_action_details || '';
                      const issueSlide = issue.ISSUE_SLIDES || issue.slide_ref || '';
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#fafbfc'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '16px 14px' }}>
                            <span style={{ fontFamily: F_FREE.mono, fontWeight: 600, color: '#94a3b8', fontSize: 12 }}>#{i + 1}</span>
                          </td>
                          <td style={{ padding: '16px 14px', wordBreak: 'break-word' }}>
                            <p style={{ fontFamily: F_FREE.display, fontSize: 13, fontWeight: 700, color: '#0f172a', lineHeight: 1.5, margin: 0 }}>{issueTitle}</p>
                          </td>
                          <td style={{ padding: '16px 14px', wordBreak: 'break-word' }}>
                            <p style={{ fontFamily: F_FREE.body, fontSize: 12, color: '#64748b', lineHeight: 1.6, margin: 0 }}>{issueDesc}</p>
                          </td>
                          <td style={{ padding: '16px 14px' }}>
                            <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontFamily: F_FREE.display, fontSize: 11, fontWeight: 700, background: st.bg, color: st.color, whiteSpace: 'nowrap' }}>{st.label}</span>
                          </td>
                          <td style={{ padding: '16px 14px' }}>
                            {issueSlide
                              ? <span style={{ fontFamily: F_FREE.mono, fontSize: 11, fontWeight: 600, color: '#004AAD', wordBreak: 'break-word' }}>↗ {issueSlide}</span>
                              : <span style={{ color: '#cbd5e1', fontSize: 13 }}>—</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div style={{ padding: '48px 28px', textAlign: 'center', color: '#94a3b8', fontSize: 14, fontFamily: F_FREE.body }}>No issues found.</div>
              )}
            </div>
          )}
          {totalPages > 1 && <PaginationBar />}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  AuditResultsScreen
// ══════════════════════════════════════════════════════════════
const AuditResultsScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userProfile, user } = useAuth();
  const contentPad = useContentPad();
  const responsive = useResponsive();

  const [showShareModal, setShowShareModal] = useState(false);
  const [reportSelectorOpen, setReportSelectorOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('summary');
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [slideshowInterval, setSlideshowInterval] = useState(null);
  const [issueFilter, setIssueFilter] = useState('all');
  const [issueCurrentPage, setIssueCurrentPage] = useState(1);

  const [auditReports, setAuditReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [currentReport, setCurrentReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [rawHtmlText, setRawHtmlText] = useState('');
  const [isLoadingHtml, setIsLoadingHtml] = useState(false);
  const [slidesData, setSlidesData] = useState([]);
  const [groupedPriorities, setGroupedPriorities] = useState({ HIGH: [], MEDIUM: [], LOW: [] });
  const [startupData, setStartupData] = useState({});
  const [slideHtmlMap, setSlideHtmlMap] = useState([]);
  const [currentSlideHtml, setCurrentSlideHtml] = useState('');
  const [recommendations, setRecommendations] = useState([]);
  const [categoryScores, setCategoryScores] = useState([]);
  const [personaInsights, setPersonaInsights] = useState([]);
  const [battleCards, setBattleCards] = useState([]);
  const [outreachSequence, setOutreachSequence] = useState([]);
  const [competitorData, setCompetitorData] = useState([]);
  const [marketData, setMarketData] = useState({});
  const [versionHistory, setVersionHistory] = useState([]);
  const [benchmarkData, setBenchmarkData] = useState({});
  const [foundersNote, setFoundersNote] = useState({});
  const [pitchCanvasData, setPitchCanvasData] = useState([]);
  const [eDocs, setEDocs] = useState([]);
  const [customPrompts, setCustomPrompts] = useState([]);
  const [deckSlideCount, setDeckSlideCount] = useState(0);

  const iframeRef = useRef(null);
  const slidePreviewRef = useRef(null);
  const mobileNavRef = useRef(null);

  useEffect(() => {
    const id = searchParams.get('reportId');
    if (id) loadSpecificReport(id);
    else loadUserAuditReports();
  }, [searchParams]);

  useEffect(() => {
    let channel;
    (async () => {
      const { data: { user: authUser } } = await supabase?.auth?.getUser();
      if (!authUser) return;
      channel = supabase?.channel('user_audit_reports')
        ?.on('postgres_changes',
          { event: '*', schema: 'public', table: 'audit_reports', filter: `user_id=eq.${authUser.id}` },
          async () => {
            const { data } = await supabase.from('audit_reports')
              .select('id, company_name, deck_name, report_type, published_at, created_at, total_score')
              .eq('user_id', authUser.id).eq('status', 'published').not('published_at', 'is', null)
              .order('published_at', { ascending: false });
            if (data) setAuditReports(data);
          })?.subscribe();
    })();
    return () => {
      if (channel) supabase?.removeChannel(channel);
      if (slideshowInterval) clearInterval(slideshowInterval);
    };
  }, []);

  useEffect(() => {
    if (!currentReport) return;
    extractReportData(currentReport);
    loadHtmlAndJsonFromStorage(currentReport);
    (async () => {
      try {
        const submissionId = currentReport.submission_id || currentReport.deck_submission_id;
        let q;
        if (submissionId) {
          q = supabase.from('deck_submissions').select('slide_count').eq('id', submissionId).maybeSingle();
        } else if (currentReport.request_id) {
          q = supabase.from('deck_submissions').select('slide_count').eq('request_id', currentReport.request_id).maybeSingle();
        }
        if (q) {
          const { data } = await q;
          if (data?.slide_count) setDeckSlideCount(Number(data.slide_count));
        }
      } catch (e) { console.warn('deckSlideCount fetch failed:', e); }
    })();
  }, [currentReport]);

  useEffect(() => {
    if (slideHtmlMap.length > 0 && slideHtmlMap[currentSlideIndex])
      setCurrentSlideHtml(slideHtmlMap[currentSlideIndex]);
  }, [currentSlideIndex, slideHtmlMap]);

  useEffect(() => { setIssueCurrentPage(1); }, [issueFilter]);

  useEffect(() => {
    if (responsive.isMobile && mobileNavRef.current) {
      const activeBtn = mobileNavRef.current.querySelector('[data-active="true"]');
      if (activeBtn) activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeSection, responsive.isMobile]);

  const sanitizeObject = (obj) => {
    if (typeof obj === 'string') {
      return obj
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/["/]/g, '')
        .trim();
    }

    if (Array.isArray(obj)) {
      return obj.map(sanitizeObject);
    }

    if (typeof obj === 'object' && obj !== null) {
      const newObj = {};
      for (let key in obj) {
        newObj[key] = sanitizeObject(obj[key]);
      }
      return newObj;
    }

    return obj;
  };

  const applyParsed = (parsed) => {
    if (!parsed) return;
    if (parsed.startupData && Object.keys(parsed.startupData).length)
      setStartupData(p => ({ ...p, ...parsed.startupData }));
    if (parsed.slides?.length) setSlidesData(parsed.slides);
    if (parsed.groupedPriorities?.HIGH?.length || parsed.groupedPriorities?.MEDIUM?.length || parsed.groupedPriorities?.LOW?.length)
      setGroupedPriorities(parsed.groupedPriorities);
    if (parsed.recommendations?.length) setRecommendations(parsed.recommendations);
    if (parsed.categoryScores?.length) setCategoryScores(parsed.categoryScores);
    if (parsed.personaInsights?.length) setPersonaInsights(parsed.personaInsights);
    if (parsed.battleCards?.length) setBattleCards(parsed.battleCards);
    if (parsed.outreachSequence?.length) setOutreachSequence(parsed.outreachSequence);
    if (parsed.competitorData?.length) setCompetitorData(parsed.competitorData);
    if (Object.keys(parsed.marketData || {}).length) setMarketData(parsed.marketData);
    if (parsed.versionHistory?.length) setVersionHistory(parsed.versionHistory);
    if (Object.keys(parsed.benchmarkData || {}).length) setBenchmarkData(parsed.benchmarkData);
    if (parsed.foundersNote && Object.keys(parsed.foundersNote).length) {
      const cleanData = sanitizeObject(parsed.foundersNote);
      setFoundersNote(cleanData);
    }
    if (parsed.pitchCanvasData?.length) setPitchCanvasData(parsed.pitchCanvasData);
    if (parsed.eDocs?.length) setEDocs(parsed.eDocs);
    if (parsed.customPrompts?.length) setCustomPrompts(parsed.customPrompts);
  };

  const extractReportData = (report) => {
    if (report?.report_data) {
      try {
        const rd = typeof report.report_data === 'string' ? JSON.parse(report.report_data) : report.report_data;
        if (rd?.startupData) setStartupData(rd.startupData);
        if (rd?.slides) setSlidesData(rd.slides);
        if (rd?.groupedPriorities) setGroupedPriorities(rd.groupedPriorities);
        if (rd?.recommendations) setRecommendations(rd.recommendations);
        if (rd?.categoryScores) setCategoryScores(rd.categoryScores);
        if (rd?.personaInsights) setPersonaInsights(rd.personaInsights);
        if (rd?.battleCards) setBattleCards(rd.battleCards);
        if (rd?.outreachSequence) setOutreachSequence(rd.outreachSequence);
        if (rd?.competitorData) setCompetitorData(rd.competitorData);
        if (rd?.marketData) setMarketData(rd.marketData);
        if (rd?.versionHistory) setVersionHistory(rd.versionHistory);
        if (rd?.benchmarkData) setBenchmarkData(rd.benchmarkData);
        if (rd?.foundersNote) setFoundersNote(rd.foundersNote);
        if (rd?.pitchCanvasData) setPitchCanvasData(rd.pitchCanvasData);
        if (rd?.eDocs) setEDocs(rd.eDocs);
        if (rd?.customPrompts) setCustomPrompts(rd.customPrompts);
      } catch (e) { console.error('report_data parse error:', e); }
    }
    if (report?.json_data) {
      try {
        const jd = typeof report.json_data === 'string' ? JSON.parse(report.json_data) : report.json_data;
        applyParsed(parseJsonReport(jd));
      } catch (e) { console.error('json_data parse error:', e); }
    }
  };

  const loadHtmlAndJsonFromStorage = async (report) => {
    setIsLoadingHtml(true);
    try {
      const isFreeReport = (report?.report_type || '').toString().toUpperCase() === 'FREE';
      if (report?.file_path) {
        try {
          const { data, error } = await supabase.storage.from('audit-files').download(report.file_path);
          if (!error && data) { 
            applyParsed(parseJsonReport(JSON.parse(await data.text())));
            // if (isFreeReport) { setIsLoadingHtml(false); return; }
          }
        } catch (e) { console.warn('file_path bucket fetch failed:', e); }
      }
      if (report?.file_url && !report.file_url.endsWith('.html')) {
        try {
          const res = await fetch(report.file_url, { cache: 'no-store' });
          if (res.ok) {
            applyParsed(parseJsonReport(await res.json()));
            // if (isFreeReport) { setIsLoadingHtml(false); return; }
          }
        } catch (e) { console.warn('file_url JSON fetch failed:', e); }
      }
      let htmlText = '';
      if (report?.html_storage_path) htmlText = await fetchHtmlFromStorage(report.html_storage_path);
      if (!htmlText && report?.html_url && !report.html_url.endsWith('.json')) {
        try { const res = await fetch(report.html_url, { cache: 'no-store' }); if (res.ok) htmlText = await res.text(); } catch (e) { console.warn('html_url fetch failed:', e); }
      }
      if (!htmlText && report?.html_content) htmlText = report.html_content;
      if (htmlText && htmlText.includes('<') && !htmlText.includes('@vite/client')) {
        applyParsed(parseReportDataFromHtml(htmlText));
        const map = extractSlideHtmlMap(htmlText);
        setSlideHtmlMap(map);
        if (map.length) setCurrentSlideHtml(map[0]);
        setRawHtmlText(htmlText);
      } else { setRawHtmlText(''); }
    } catch (e) { console.error('loadHtmlAndJsonFromStorage error:', e); setRawHtmlText(''); }

    finally {
       setIsLoadingHtml(false); }
  };

  const loadSpecificReport = async (reportId) => {
    if (!user?.id) { setIsLoading(false); return; }
    try {
      setIsLoading(true); setError(''); setRawHtmlText('');
      const { data, error: e } = await supabase.from('audit_reports').select('*').eq('id', reportId).maybeSingle();
      if (e) throw e;
      if (data) { setCurrentReport(data); setSelectedReportId(data.id); }
      else setError('Report not found');
      supabase.from('audit_reports')
        .select('id, company_name, deck_name, report_type, published_at, created_at, total_score')
        .eq('user_id', user.id).eq('status', 'published').not('published_at', 'is', null)
        .order('published_at', { ascending: false })
        .then(({ data: d }) => { if (d) setAuditReports(d); });
    } catch (e) { console.error(e); setError('Failed to load audit report'); }
    finally { setIsLoading(false); }
  };

  const loadUserAuditReports = async () => {
    if (!user?.id) { setIsLoading(false); return; }
    try {
      setIsLoading(true); setError('');
      const { data, error: e } = await supabase.from('audit_reports')
        .select('id, company_name, deck_name, report_type, published_at, created_at, total_score')
        .eq('user_id', user.id).eq('status', 'published').not('published_at', 'is', null)
        .order('published_at', { ascending: false });
      if (e) throw e;
      setAuditReports(data || []);
      if (data?.length > 0) { setSelectedReportId(data[0].id); await loadAuditReportById(data[0].id); }
      else setIsLoading(false);
    } catch (e) { console.error(e); setError('Failed to load audit reports'); setIsLoading(false); }
  };

  const loadAuditReportById = async (reportId) => {
    try {
      setIsLoading(true); setError(''); setRawHtmlText('');
      const data = await auditReportService?.getAuditReportById(reportId);
      setCurrentReport(data);
    } catch (e) { console.error(e); setError('Failed to load audit report'); }
    finally { setIsLoading(false); }
  };

  const handleReportChange = (reportId) => {
    setSelectedReportId(reportId); setReportSelectorOpen(false); setActiveSection('summary');
    setCurrentSlideIndex(0); setRawHtmlText('');
    setSlidesData([]); setGroupedPriorities({ HIGH: [], MEDIUM: [], LOW: [] }); setStartupData({});
    setSlideHtmlMap([]); setCurrentSlideHtml(''); setRecommendations([]);
    setCategoryScores([]); setPersonaInsights([]); setBattleCards([]);
    setOutreachSequence([]); setCompetitorData([]); setMarketData({});
    setVersionHistory([]); setBenchmarkData({}); setFoundersNote({});
    setPitchCanvasData([]); setEDocs([]); setCustomPrompts([]); setDeckSlideCount(0);
    loadAuditReportById(reportId);
  };

  const handleDownload = () => {
    const content = rawHtmlText || currentReport?.html_content;
    console.log(rawHtmlText);
    if (content) {
      const blob = new Blob([content], { type: 'text/html; charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = currentReport?.html_storage_path?.split('/')?.pop() || currentReport?.file_name || 'audit_report.html';
      a.click();
      URL.revokeObjectURL(url);
    } else { alert('No HTML report available for download yet.'); }
  };

  const handlePlaySlideshow = () => {
    if (slideshowInterval) { clearInterval(slideshowInterval); setSlideshowInterval(null); return; }
    const iv = setInterval(() => setCurrentSlideIndex(p => (p + 1) % slidesData.length), 3000);
    setSlideshowInterval(iv);
  };

  const handleSlideClick = (index) => {
    if (slideshowInterval) { clearInterval(slideshowInterval); setSlideshowInterval(null); }
    setCurrentSlideIndex(index);
    if (responsive.isMobile && slidePreviewRef.current)
      slidePreviewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(window.location?.href);
    alert('Link copied!');
    setShowShareModal(false);
  };

  const iltcNum = getScoreNum(startupData?.iltc_score);
  const reportType = (currentReport?.report_type || '').toString().toUpperCase();
  const isFree = reportType === 'FREE' || reportType === '';

  // ─────────────────────────────────────────────────────────
  // SectionWrap
  // ─────────────────────────────────────────────────────────
  const SectionWrap = ({ children, noPad = false }) => (
    <div style={{
      background: '#ffffff',
      padding: noPad ? 0 : contentPad,
      width: '100%',
      boxSizing: 'border-box',
      minWidth: 0,
    }}>
      {children}
    </div>
  );

  // ── Content section renderer ──────────────────────────────
  const renderSection = () => {
    switch (activeSection) {
      case 'founders-note':
        return <SectionWrap><FoundersNote foundersNote={foundersNote} /></SectionWrap>;

      case 'mypitch-canvas':
        return <SectionWrap><PitchCanvas pitchCanvasData={pitchCanvasData} startupData={startupData} /></SectionWrap>;

      case 'summary':
        return (
          <SectionWrap>
            <ScoreOverview
              iltcNum={iltcNum} startupData={startupData} slidesData={slidesData}
              groupedPriorities={groupedPriorities} categoryScores={categoryScores}
              setActiveSection={setActiveSection}
            />
          </SectionWrap>
        );

      case 'all-issues':
        return (
          <SectionWrap>
            {!isFree
              ? <AllIssues groupedPriorities={groupedPriorities} />
              : (
                <FreeAllIssues
                  groupedPriorities={groupedPriorities}
                  issueFilter={issueFilter}
                  setIssueFilter={(f) => { setIssueFilter(f); setIssueCurrentPage(1); }}
                  issueCurrentPage={issueCurrentPage}
                  setIssueCurrentPage={setIssueCurrentPage}
                />
              )
            }
          </SectionWrap>
        );

      case 'slides':
        return (
          <SectionWrap>
            {isFree ? (
              <>
                <SlideAnalysis
                  slidesData={slidesData.slice(0, 3)}
                  slideHtmlMap={slideHtmlMap} currentSlideIndex={currentSlideIndex}
                  currentSlideHtml={currentSlideHtml} isLoadingHtml={isLoadingHtml}
                  slideshowInterval={slideshowInterval} iframeRef={iframeRef}
                  slidePreviewRef={slidePreviewRef} handleSlideClick={handleSlideClick}
                  handlePlaySlideshow={handlePlaySlideshow}
                />
                {slidesData.length > 3 && (
                  <div style={{ marginTop: 24 }}>
                    <PremiumGate locked={true} sectionId="slides">{null}</PremiumGate>
                  </div>
                )}
              </>
            ) : (
              <SlideAnalysis
                slidesData={slidesData} slideHtmlMap={slideHtmlMap}
                currentSlideIndex={currentSlideIndex} currentSlideHtml={currentSlideHtml}
                isLoadingHtml={isLoadingHtml} slideshowInterval={slideshowInterval}
                iframeRef={iframeRef} slidePreviewRef={slidePreviewRef}
                handleSlideClick={handleSlideClick} handlePlaySlideshow={handlePlaySlideshow}
              />
            )}
          </SectionWrap>
        );

      case 'recommendations':
        return (
          <SectionWrap>
            {isFree
              ? <PremiumGate locked={true} sectionId="recommendations">{null}</PremiumGate>
              : <Recommendations recommendations={recommendations} handleDownload={handleDownload} />
            }
          </SectionWrap>
        );

      case 'versions':
        return (
          <SectionWrap>
            {isFree ? (
              <PremiumGate locked={true} sectionId="versions">{null}</PremiumGate>
            ) : versionHistory.length > 0 ? (
              <>
                <h2 style={{ fontSize: responsive.isMobile ? 18 : 22, fontWeight: 800, color: '#0f172a', margin: '0 0 20px', fontFamily: "'Sansation', sans-serif", letterSpacing: '-0.03em' }}>
                  Version History
                </h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {versionHistory.map((v, i) => (
                    <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: responsive.isMobile ? '12px 14px' : '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ fontSize: responsive.isMobile ? 13 : 14, fontWeight: 600, color: '#0f172a', margin: 0, wordBreak: 'break-word' }}>{v.name || `Version ${i + 1}`}</p>
                        {v.date && <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, marginBottom: 0 }}>{v.date}</p>}
                      </div>
                      {v.score && (
                        <span style={{ fontSize: 14, fontWeight: 700, background: GRADIENT, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', flexShrink: 0 }}>{v.score}</span>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: responsive.isMobile ? '60px 20px' : '80px 0', textAlign: 'center' }}>
                <div style={{ width: responsive.isMobile ? 56 : 64, height: responsive.isMobile ? 56 : 64, borderRadius: 16, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <Icon name="Sparkles" size={responsive.isMobile ? 24 : 28} color="#94a3b8" />
                </div>
                <h3 style={{ fontSize: responsive.isMobile ? 14 : 15, fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>Coming Soon</h3>
                <p style={{ fontSize: responsive.isMobile ? 12 : 13, color: '#94a3b8', maxWidth: 360, margin: 0 }}>Version history tracking will be available in a future update.</p>
              </div>
            )}
          </SectionWrap>
        );

      case 'benchmark':
        return (
          <SectionWrap>
            {isFree
              ? <PremiumGate locked={true} sectionId="benchmark">{null}</PremiumGate>
              : <CompetitorBenchmark categoryScores={categoryScores} benchmarkData={benchmarkData} competitorData={competitorData} />
            }
          </SectionWrap>
        );

      case 'industry-trends':
        return (
          <SectionWrap>
            {isFree
              ? <PremiumGate locked={true} sectionId="industry-trends">{null}</PremiumGate>
              : <IndustryTrends marketData={marketData} />
            }
          </SectionWrap>
        );

      case 'persona-outputs':
        return (
          <SectionWrap>
            {isFree
              ? <PremiumGate locked={true} sectionId="persona-outputs">{null}</PremiumGate>
              : <PersonaMatch personaInsights={personaInsights} handleDownload={handleDownload} />
            }
          </SectionWrap>
        );

      case 'market-analysis':
        return (
          <SectionWrap>
            {isFree
              ? <PremiumGate locked={true} sectionId="market-analysis">{null}</PremiumGate>
              : <MarketAnalysis marketData={marketData} startupData={startupData} />
            }
          </SectionWrap>
        );

      case 'outreach-sequence':
        return (
          <SectionWrap>
            {isFree
              ? <PremiumGate locked={true} sectionId="outreach-sequence">{null}</PremiumGate>
              : <OutreachSequence outreachSequence={outreachSequence} />
            }
          </SectionWrap>
        );

      case 'battle-cards':
        return (
          <SectionWrap>
            {isFree
              ? <PremiumGate locked={true} sectionId="battle-cards">{null}</PremiumGate>
              : <BattleCards battleCards={battleCards} />
            }
          </SectionWrap>
        );

      case 'key-documents':
        return (
          <SectionWrap>
            {isFree
              ? <PremiumGate locked={true} sectionId="key-documents">{null}</PremiumGate>
              : <KeyDocuments currentReport={currentReport} eDocs={eDocs} />
            }
          </SectionWrap>
        );

      case 'custom-prompt':
        return (
          <SectionWrap>
            {isFree
              ? <PremiumGate locked={true} sectionId="custom-prompt">{null}</PremiumGate>
              : <CustomPrompts customPrompts={customPrompts} />
            }
          </SectionWrap>
        );

      case 'revised-deck':
        return (
          <SectionWrap>
            {isFree ? (
              <PremiumGate locked={true} sectionId="revised-deck">{null}</PremiumGate>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', minHeight: responsive.isMobile ? 300 : 400, padding: responsive.isMobile ? '0 20px' : 0 }}>
                <span style={{ fontSize: responsive.isMobile ? 40 : 48, lineHeight: 1, marginBottom: 16 }}>📦</span>
                <h3 style={{ fontSize: responsive.isMobile ? 15 : 16, fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>Revised Deck Coming Soon</h3>
                <p style={{ fontSize: responsive.isMobile ? 13 : 14, color: '#94a3b8', margin: 0, maxWidth: 420, lineHeight: 1.6 }}>
                  Apply the recommendations above, then upload your revised deck to unlock this section.
                </p>
              </div>
            )}
          </SectionWrap>
        );

      default:
        return null;
    }
  };

  // ── Loading state ──
  if (isLoading) return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader userRole={userProfile?.role || 'user'} pendingAudits={0} />
      <AnnouncementBanner />
      <main><div className="max-w-screen-2xl mx-auto px-4 py-4">
        <div className="text-center py-20">
          <Icon name="Loader" size={responsive.isMobile ? 40 : 48} color="var(--color-primary)" className="animate-spin mx-auto mb-4" />
          <p className="text-lg text-muted-foreground" style={{ fontSize: responsive.isMobile ? 14 : 16 }}>Loading your audit report...</p>
        </div>
      </div></main>
    </div>
  );

  // ── Error state ──
  if (error) return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader userRole={userProfile?.role || 'user'} pendingAudits={0} />
      <AnnouncementBanner />
      <main><div className="max-w-screen-2xl mx-auto px-4 py-4">
        <div className="flex items-center gap-3 p-4 bg-error/10 border border-error/20 rounded-lg">
          <Icon name="AlertCircle" size={20} color="var(--color-error)" />
          <span className="text-sm text-error font-medium" style={{ fontSize: responsive.isMobile ? 13 : 14 }}>{error}</span>
        </div>
      </div></main>
    </div>
  );

  // ── Empty state ──
  if (!currentReport) return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader userRole={userProfile?.role || 'user'} pendingAudits={0} />
      <AnnouncementBanner />
      <main><div className="max-w-screen-2xl mx-auto px-4 py-4">
        <div className="bg-card rounded-2xl border border-border p-12 text-center" style={{ padding: responsive.isMobile ? '40px 20px' : '48px' }}>
          <Icon name="FileText" size={responsive.isMobile ? 56 : 64} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2" style={{ fontSize: responsive.isMobile ? 17 : 20 }}>No Audit Report Available</h3>
          <p className="text-sm text-muted-foreground mb-6" style={{ fontSize: responsive.isMobile ? 13 : 14 }}>Your audit report will appear here once the admin publishes it.</p>
          <button onClick={() => navigate('/user-dashboard')} className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ background: GRADIENT, fontSize: responsive.isMobile ? 13 : 14 }}>
            Return to Dashboard
          </button>
        </div>
      </div></main>
    </div>
  );

  // ── Main render ──
  return (
    <div className="min-h-screen bg-background" style={{ overflowX: 'hidden', maxWidth: '100vw' }}>
      <RoleBasedHeader userRole={userProfile?.role || 'user'} pendingAudits={0} />
      <AnnouncementBanner />

      <main className="ars-main" style={{ background: 'rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', overflowX: 'hidden', maxWidth: '100vw' }}>
        <style>{`
          .ars-header { flex-shrink: 0; background: #ffffff; }

          .ars-body {
            display: grid;
            grid-template-columns: 280px minmax(0, 1fr);
            gap: 2px;
            flex: 1;
            min-height: 0;
            overflow: hidden;
          }

          .ars-sidebar {
            overflow-y: auto;
            overflow-x: hidden;
            height: 100%;
            scrollbar-width: thin;
            scrollbar-color: #e2e8f0 transparent;
          }
          .ars-sidebar::-webkit-scrollbar { width: 4px; }
          .ars-sidebar::-webkit-scrollbar-track { background: transparent; }
          .ars-sidebar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }

          .ars-mob-nav { display: none !important; }

          .ars-content {
            min-width: 0;
            width: 100%;
            box-sizing: border-box;
            overflow-y: auto;
            overflow-x: hidden;
            height: 100%;
            background: rgba(0,0,0,0.04);
            display: flex;
            flex-direction: column;
            gap: 2px;
            padding-bottom: 0;
            scrollbar-width: thin;
            scrollbar-color: #e2e8f0 transparent;
          }
          .ars-content::-webkit-scrollbar { width: 6px; }
          .ars-content::-webkit-scrollbar-track { background: transparent; }
          .ars-content::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }

          .ars-content > * {
            min-width: 0;
            width: 100%;
            box-sizing: border-box;
          }

          /* Disclaimer wrapper — sits flush at bottom of content panel */
          .ars-disclaimer {
            flex-shrink: 0;
            padding: 2px 2px 0;
            margin-top: auto;
          }

          .ars-main {
            height: calc(100vh - 56px);
            overflow: hidden;
          }

          .ars-mob-scroll {
            display: flex;
            gap: 0;
            overflow-x: auto;
            overflow-y: hidden;
            scrollbar-width: none;
            -ms-overflow-style: none;
            -webkit-overflow-scrolling: touch;
            padding: 0 4px;
          }
          .ars-mob-scroll::-webkit-scrollbar { 
            display: none; 
            height: 0;
            width: 0;
          }

          @media (min-width: 0px) and (max-width: 1023px) {
            .ars-mob-scroll {
              scrollbar-width: thin;
              -ms-overflow-style: auto;
            }
            .ars-mob-scroll::-webkit-scrollbar { 
              display: block; 
              height: 6px;
            }
            .ars-mob-scroll::-webkit-scrollbar-track { background: transparent; }
            .ars-mob-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 4px; }
            .ars-mob-scroll::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.2); }
          }

          @media (max-width: 1023px) {
            .ars-main {
              height: auto !important;
              overflow: visible !important;
              min-height: calc(100dvh - 56px);
            }
            .ars-body {
              grid-template-columns: 1fr;
              flex: unset;
              overflow: visible;
              min-height: unset;
              width: 100vw;
              max-width: 100%;
            }
            .ars-sidebar { display: none !important; }
            .ars-mob-nav { 
              display: block !important;
              width: 100vw;
              max-width: 100%;
              position: sticky;
              top: 0;
              left: 0;
              right: 0;
              overflow: hidden;
            }
            .ars-mob-scroll {
              width: 100%;
              max-width: 100vw;
              overflow-x: auto;
              overflow-y: hidden;
              -webkit-overflow-scrolling: touch;
              scroll-behavior: smooth;
              padding: 0 8px;
            }
            .ars-content {
              overflow-y: visible !important;
              overflow-x: hidden;
              height: auto !important;
              padding-bottom: 0;
              width: 100vw;
              max-width: 100%;
            }
            .ars-content > * {
              overflow: visible;
              width: 100%;
              max-width: 100vw;
              box-sizing: border-box;
            }
            /* On mobile, disclaimer doesn't need auto margin */
            .ars-disclaimer {
              margin-top: 2px;
              padding: 2px 2px 0;
              width: 100%;
              max-width: 100vw;
            }
          }

          /* Ensure no horizontal overflow on mobile */
          @media (max-width: 1023px) {
            html, body {
              overflow-x: hidden;
              max-width: 100vw;
            }
            * {
              max-width: 100%;
            }
          }
        `}</style>

        {/* Results header */}
        <div className="ars-header">
          <ResultsHeader
            reportSelectorOpen={reportSelectorOpen} setReportSelectorOpen={setReportSelectorOpen}
            startupData={startupData} currentReport={currentReport}
            slidesData={slidesData} iltcNum={iltcNum}
            auditReports={auditReports} selectedReportId={selectedReportId}
            handleReportChange={handleReportChange} handleDownload={handleDownload}
            deckSlideCount={deckSlideCount}
          />
        </div>

        <div className="ars-body">

          {/* Desktop sidebar */}
          <div className="ars-sidebar">
            <ResultsSidebar
              activeSection={activeSection} setActiveSection={setActiveSection}
              iltcNum={iltcNum} startupData={startupData} slidesData={slidesData}
            />
          </div>

          {/* Mobile horizontal nav */}
          <div className="ars-mob-nav" style={{
            background: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            position: 'sticky',
            top: 0,
            zIndex: 50,
            width: '100%',
            overflowX: 'hidden',
          }}>
            <div ref={mobileNavRef} className="ars-mob-scroll" style={{
              display: 'flex',
              gap: 0,
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              padding: '0 8px',
              width: '100%',
            }}>
              {sidebarSections.flatMap(g => g.items).filter(i => !i.disabled).map(item => {
                const active = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    data-active={active}
                    onClick={() => setActiveSection(item.id)}
                    style={{
                      flexShrink: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: responsive.isMobile ? '10px 12px' : '11px 14px',
                      border: 'none',
                      borderBottom: active ? '2px solid #004AAD' : '2px solid transparent',
                      background: 'transparent',
                      color: active ? '#004AAD' : '#6e6e73',
                      fontSize: responsive.isMobile ? 11.5 : 12.5,
                      fontWeight: active ? 600 : 500,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      whiteSpace: 'nowrap',
                      transition: 'color 0.15s, border-color 0.15s',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <Icon name={item.icon} size={responsive.isMobile ? 12 : 13} color={active ? '#004AAD' : '#94a3b8'} />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content panel */}
          <div className="ars-content">

            {/* Active section */}
            {renderSection()}

            {/* ── Disclaimer — always at the bottom of the scroll area ── */}
            <div className="ars-disclaimer">
              <AuditDisclaimer />
            </div>

          </div>

        </div>
      </main>

      {/* Share modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1010] p-4">
          <div className="bg-card rounded-2xl p-6 max-w-md w-full shadow-xl" style={{ 
            padding: responsive.isMobile ? '20px' : '24px',
            maxWidth: responsive.isMobile ? '90%' : '448px'
          }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground" style={{ fontSize: responsive.isMobile ? 16 : 18 }}>Share Results</h3>
              <button onClick={() => setShowShareModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted">
                <Icon name="X" size={20} />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-4" style={{ fontSize: responsive.isMobile ? 13 : 14 }}>Share your audit results with team members or stakeholders</p>
            <div className="space-y-3">
              <button onClick={handleCopyLink} className="w-full flex items-center gap-3 p-3 bg-muted rounded-xl hover:bg-muted/80 text-left" style={{ padding: responsive.isMobile ? '10px 12px' : '12px' }}>
                <Icon name="Link" size={20} color="var(--color-primary)" />
                <div>
                  <div className="text-sm font-medium text-foreground" style={{ fontSize: responsive.isMobile ? 13 : 14 }}>Copy Link</div>
                  <div className="text-xs text-muted-foreground" style={{ fontSize: responsive.isMobile ? 11 : 12 }}>Share via URL</div>
                </div>
              </button>
              <button onClick={() => alert('Email sharing')} className="w-full flex items-center gap-3 p-3 bg-muted rounded-xl hover:bg-muted/80 text-left" style={{ padding: responsive.isMobile ? '10px 12px' : '12px' }}>
                <Icon name="Mail" size={20} color="var(--color-primary)" />
                <div>
                  <div className="text-sm font-medium text-foreground" style={{ fontSize: responsive.isMobile ? 13 : 14 }}>Email</div>
                  <div className="text-xs text-muted-foreground" style={{ fontSize: responsive.isMobile ? 11 : 12 }}>Send via email</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditResultsScreen;